import { Helmet } from 'react-helmet-async';

const faqs = [
  {
    q: 'What does Blackmagic Films specialize in?',
    a: 'Blackmagic Films specializes in cinematic music videos, branded content, wedding films, concert and live-event videography, and artist studio sessions. The company also offers portrait and editorial photography for musicians, brands, and creative clients.',
  },
  {
    q: 'Where is Blackmagic Films located?',
    a: 'Blackmagic Films is based in Bucks County, Pennsylvania. The company regularly works on-location throughout Pennsylvania, New Jersey, Philadelphia, and New York City, and is available for travel to other markets on request.',
  },
  {
    q: 'What camera equipment does Blackmagic Films use?',
    a: 'All video productions are shot natively on the Blackmagic Cinema Camera 6K, a professional cinema-grade camera that captures 6K resolution RAW and ProRes footage with a wide dynamic range. This camera system is used by major film and television productions worldwide and produces the distinctive cinematic look Blackmagic Films is known for.',
  },
  {
    q: 'How long has Blackmagic Films been in business?',
    a: 'Blackmagic Films was founded in 2019 by Mike, a director and cinematographer based in Bucks County, PA. The company has since worked with independent recording artists, regional brands, event promoters, and small businesses across the tri-state area.',
  },
  {
    q: 'What types of clients does Blackmagic Films work with?',
    a: 'Blackmagic Films works with independent and signed recording artists, music labels, small businesses, local brands, wedding couples, event promoters, and creative agencies. Past clients include artists affiliated with Epitaph Records, regional hip-hop and alternative acts, and Kevin Smith\'s The Secret Stash in Red Bank, NJ.',
  },
  {
    q: 'What is the video production process from brief to delivery?',
    a: 'A typical Blackmagic Films project begins with a discovery call to align on creative goals, budget, and timeline. This is followed by pre-production planning (concept, shot list, location scouting, and scheduling), the production shoot day(s), and then post-production editing including color grading, sound design, and revisions. Final deliverables are provided in the agreed format — typically H.264 or ProRes masters for digital distribution.',
  },
  {
    q: 'Does Blackmagic Films travel outside New Jersey?',
    a: 'Yes. While based in Rutherford, NJ, Blackmagic Films regularly shoots in Philadelphia, New York City, and throughout the tri-state region. Travel to other states and markets is available and quoted on a per-project basis.',
  },
  {
    q: 'How do I book Blackmagic Films for a project?',
    a: 'To start a project, email mike@blackmagicfilms.com with a brief description of your project, desired dates, and location. You can also reach out through the Work With Me page on this site. Response time is typically within 24–48 hours.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: a,
    },
  })),
};

export default function FAQ() {
  return (
    <>
      <Helmet>
        <title>FAQ — Blackmagic Films | NJ Video Production</title>
        <meta
          name="description"
          content="Frequently asked questions about Blackmagic Films — a New Jersey-based cinematic video production company founded in 2019. Learn about services, equipment, pricing, and booking."
        />
        <link rel="canonical" href="https://www.blackmagicfilms.com/faq" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.blackmagicfilms.com/" },
            { "@type": "ListItem", "position": 2, "name": "FAQ", "item": "https://www.blackmagicfilms.com/faq" }
          ]
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-black text-white pt-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-10 py-16 md:py-24">
          <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">Help</span>
          <h1 className="text-3xl sm:text-4xl font-black mt-2 mb-3">Frequently Asked Questions</h1>
          <p className="text-white/50 mb-12 text-base">
            Common questions about Blackmagic Films, our services, equipment, and booking process.
          </p>

          <div className="space-y-8">
            {faqs.map(({ q, a }) => (
              <div key={q} className="border-t border-white/10 pt-8">
                <h2 className="text-lg font-bold mb-3">{q}</h2>
                <p className="text-white/70 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 border-t border-white/10 pt-10 text-center">
            <p className="text-white/50 mb-4">Still have questions?</p>
            <a
              href="mailto:mike@blackmagicfilms.com"
              className="inline-block px-6 py-3 border border-amber-400 text-amber-400 rounded-full text-sm font-bold tracking-widest uppercase hover:bg-amber-400 hover:text-black transition"
            >
              Email Mike
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
